import React, { useState, useEffect } from 'react';
import { Settings, Clock, User as UserIcon, Bookmark, AlertCircle, Send, Zap, Users } from 'lucide-react';
import { userProfile, auth, analysisService, admin } from '../api';
import ManageInvitationsPage from './ManageInvitationsPage';
import ManageUsersPage from './ManageUsersPage';
import ManagePlansPage from './ManagePlansPage';
import config from '../config';

const ProfilePage = ({ user, handleLogout }) => {
  const [billingDate, setBillingDate] = useState(null);
  const [activeTab, setActiveTab] = useState('preferences');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loadingAnalysisDetails, setLoadingAnalysisDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    diet_type: '',
    protein_preference: '',
    carb_preference: '',
    fat_preference: '',
    additional_notes: ''
  });
  const [restrictions, setRestrictions] = useState([]);
  const [availableRestrictions, setAvailableRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingRestrictions, setSavingRestrictions] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(true);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: false,
    marketing_emails: false
  });
  const [creditUsage, setCreditUsage] = useState({
    used: 0,
    remaining: 0,
    total: 0,
    percentageUsed: 0
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const versionFetchedRef = React.useRef(false);

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  // Fetch version info when admin status is determined (only once per session)
  useEffect(() => {
    if (isAdmin && !versionFetchedRef.current) {
      fetchVersionInfo();
      versionFetchedRef.current = true;
    }
  }, [isAdmin]);

  // Function to fetch user data (preferences, restrictions, history)
  const fetchUserData = async () => {
    setLoading(true);
    try {
      const [userResponse, prefsResponse, restrictionsResponse, historyResponse, availableRestrictionsResponse, usageStatsResponse] = await Promise.all([
        auth.getCurrentUser(),
        userProfile.getDietaryPreferences(),
        userProfile.getDietaryRestrictions(),
        userProfile.getAnalysisHistory(),
        userProfile.getAvailableRestrictions(),
        userProfile.getUsageStats()
      ]);
      
      // Set admin status from user response
      setIsAdmin(userResponse.data.is_admin || false);
      
      // Set email verification status
      setIsEmailVerified(userResponse.data.is_email_verified || false);
      
      // Set billing date if available
      if (userResponse.data.billing_date) {
        setBillingDate(new Date(userResponse.data.billing_date));
      }
      
      setPreferences(prefsResponse.data || {
        diet_type: '',
        protein_preference: '',
        carb_preference: '',
        fat_preference: '',
        additional_notes: ''
      });
      
      // Set notification settings from user response
      setNotificationSettings({
        email_notifications: userResponse.data.email_notifications || false,
        marketing_emails: userResponse.data.marketing_emails || false
      });

      // Set credit usage from usage stats endpoint
      const usageData = usageStatsResponse.data;
      if (usageData.is_unlimited) {
        setCreditUsage({
          used: usageData.credits_used,
          remaining: 'Unlimited',
          total: 'Unlimited',
          percentageUsed: Math.min(usageData.credits_used / 100, 100)  // Just for visual representation
        });
      } else {
        const percentageUsed = usageData.credits_total > 0 
          ? Math.round((usageData.credits_used / usageData.credits_total) * 100) 
          : 0;
        
        setCreditUsage({
          used: usageData.credits_used,
          remaining: usageData.credits_total - usageData.credits_used,
          total: usageData.credits_total,
          percentageUsed: percentageUsed
        });
      }
      
      setRestrictions(restrictionsResponse.data || []);
      setAnalysisHistory(historyResponse.data || []);
      setAvailableRestrictions(availableRestrictionsResponse.data || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setErrorMessage('Failed to load your profile data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch version information
  const fetchVersionInfo = async () => {
    try {
      const response = await admin.getVersion();
      setVersionInfo(response.data);
    } catch (error) {
      console.error('Error fetching version info:', error);
      // Silently fail - version info is not critical
    }
  };

  // Function to update dietary preferences
  const updateDietaryPreferences = async () => {
    setSavingPrefs(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Create a sanitized copy of preferences where empty strings are converted to null
      const sanitizedPreferences = Object.fromEntries(
        Object.entries(preferences).map(([key, value]) => [key, value === '' ? null : value])
      );

      await userProfile.updateDietaryPreferences(sanitizedPreferences);
      setSuccessMessage('Dietary preferences updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating preferences:', error);
      setErrorMessage('Failed to update preferences. Please try again.');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Function to update dietary restrictions
  const updateDietaryRestrictions = async () => {
    setSavingRestrictions(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      await userProfile.updateDietaryRestrictions(restrictions.map(r => r.id));
      setSuccessMessage('Dietary restrictions updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating restrictions:', error);
      setErrorMessage('Failed to update restrictions. Please try again.');
    } finally {
      setSavingRestrictions(false);
    }
  };

  // Function to handle toggling a restriction
  const toggleRestriction = (restriction) => {
    // Check if the restriction is already selected
    const isSelected = restrictions.some(r => r.id === restriction.id);
    
    if (isSelected) {
      // Remove it from the selected restrictions
      setRestrictions(restrictions.filter(r => r.id !== restriction.id));
    } else {
      // Add it to the selected restrictions
      setRestrictions([...restrictions, restriction]);
    }
  };

  // Function to handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPreferences({
      ...preferences,
      [name]: value
    });
  };

  // Function to update notification settings
  const updateNotificationSettings = async () => {
    setSavingNotifications(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      await userProfile.updateNotificationSettings(notificationSettings);
      setSuccessMessage('Notification settings updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setErrorMessage('Failed to update notification settings. Please try again.');
    } finally {
      setSavingNotifications(false);
    }
  };

  // Function to handle account deletion
  const confirmDeleteAccount = () => {
    setShowDeleteConfirmation(true);
  };

  const cancelDeleteAccount = () => {
    setShowDeleteConfirmation(false);
  };

  // Helper function to get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)  
const getOrdinalSuffix = (number) => {
    const j = number % 10;
    const k = number % 100;
    if (j === 1 && k !== 11) {
      return 'st';
    }
    if (j === 2 && k !== 12) {
      return 'nd';
    }
    if (j === 3 && k !== 13) {
      return 'rd';
    }
    return 'th';
  };

const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setErrorMessage('');
    
    try {
      // Set success message in localStorage with a special flag to ensure it's displayed
      localStorage.setItem('auth_message', 'Your account has been successfully deleted. We hope to see you again soon!');
      // Set a flag to indicate account deletion was successful
      localStorage.setItem('account_deleted', 'true');
      
      await userProfile.deleteAccount();
      
      // Make sure to remove the token before redirecting
      localStorage.removeItem('token');
      
      // Redirect to home page
      window.location.href = '/?deleted=true';
    } catch (error) {
      // Handle 204 response (success with no content)
      if (error.response && error.response.status === 204) {
        // Make sure to remove the token before redirecting
        localStorage.removeItem('token');
        window.location.href = '/?deleted=true';
      } else {
        // Real error occurred
        setErrorMessage('Failed to delete account. Please try again later.');
        setDeletingAccount(false);
        // Remove the success message since deletion failed
        localStorage.removeItem('auth_message');
        localStorage.removeItem('account_deleted');
      }
    }
  };

  // Function to view analysis details
  const viewAnalysisDetails = async (analysisId) => {
    setLoadingAnalysisDetails(true);
    try {
      const response = await analysisService.getAnalysisDetails(analysisId);
      
      // Sort recommendations by match score in descending order
      if (response.data.recommendations && response.data.recommendations.length > 0) {
        response.data.recommendations.sort((a, b) => b.match_score - a.match_score);
      }
      
      setSelectedAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching analysis details:', error);
      setErrorMessage('Failed to load analysis details. Please try again later.');
    } finally {
      setLoadingAnalysisDetails(false);
    }
  };

  // Function to close analysis details modal
  const closeAnalysisDetails = () => {
    setSelectedAnalysis(null);
  };

  // Function to resend verification email
  const handleResendVerification = async () => {
    try {
      setSendingVerification(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      await auth.resendVerification(user.email);
      setSuccessMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setErrorMessage('Failed to send verification email. Please try again later.');
      console.error('Error sending verification email:', error);
    } finally {
      setSendingVerification(false);
    }
  };

  return (
    <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          Profile
          {isAdmin && (
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-purple-400" fill="currentColor" viewBox="0 0 8 8">
                <circle cx="4" cy="4" r="3" />
              </svg>
              Admin
            </span>
          )}
        </h1>
      </div>
      
      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {errorMessage}
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center">
          <Bookmark className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 ${isAdmin ? 'w-1/5' : 'w-1/3'} text-center ${activeTab === 'preferences' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('preferences')}
          >
            <UserIcon className="inline mr-1 h-4 w-4" />
            Preferences
          </button>
          <button
            className={`px-4 py-2 ${isAdmin ? 'w-1/5' : 'w-1/3'} text-center ${activeTab === 'history' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('history')}
          >
            <Clock className="inline mr-1 h-4 w-4" />
            Analysis History
          </button>
          <button
            className={`px-4 py-2 ${isAdmin ? 'w-1/5' : 'w-1/3'} text-center ${activeTab === 'account' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('account')}
          >
            <Settings className="inline mr-1 h-4 w-4" />
            Account
          </button>
          {isAdmin && (
            <>
              <button
                className={`px-4 py-2 w-1/5 text-center ${activeTab === 'invitations' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('invitations')}
              >
                <Send className="inline mr-1 h-4 w-4" />
                Invitations
              </button>
              <button
                className={`px-4 py-2 w-1/5 text-center ${activeTab === 'users' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('users')}
              >
                <Users className="inline mr-1 h-4 w-4" />
                Users
              </button>
              <button
                className={`px-4 py-2 w-1/5 text-center ${activeTab === 'plans' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('plans')}
              >
                <Zap className="inline mr-1 h-4 w-4" />
                Plans
              </button>
            </>
          )}
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <svg className="animate-spin h-8 w-8 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-2 text-gray-600">Loading your profile...</span>
            </div>
          ) : (
            <>
              {/* Dietary Preferences Tab */}
              {activeTab === 'preferences' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Dietary Preferences</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="diet-type" className="block text-sm font-medium text-gray-700 mb-1">
                            Diet Type
                          </label>
                          <select
                            id="diet-type"
                            name="diet_type"
                            value={preferences.diet_type || ''}
                            onChange={handleInputChange}
                            className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                          >
                            <option value="">Select a diet type</option>
                            <option value="no_preference">No Preference</option>
                            <option value="vegetarian">Vegetarian</option>
                            <option value="vegan">Vegan</option>
                            <option value="pescatarian">Pescatarian</option>
                            <option value="keto">Keto</option>
                            <option value="paleo">Paleo</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="protein-preference" className="block text-sm font-medium text-gray-700 mb-1">
                            Protein Preference
                          </label>
                          <select
                            id="protein-preference"
                            name="protein_preference"
                            value={preferences.protein_preference || ''}
                            onChange={handleInputChange}
                            className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                          >
                            <option value="">None specified</option>
                            <option value="low">Low Protein</option>
                            <option value="moderate">Moderate Protein</option>
                            <option value="high">High Protein</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="carb-preference" className="block text-sm font-medium text-gray-700 mb-1">
                            Carbohydrate Preference
                          </label>
                          <select
                            id="carb-preference"
                            name="carb_preference"
                            value={preferences.carb_preference || ''}
                            onChange={handleInputChange}
                            className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                          >
                            <option value="">None specified</option>
                            <option value="low">Low Carb</option>
                            <option value="moderate">Moderate Carb</option>
                            <option value="high">High Carb</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="fat-preference" className="block text-sm font-medium text-gray-700 mb-1">
                            Fat Preference
                          </label>
                          <select
                            id="fat-preference"
                            name="fat_preference"
                            value={preferences.fat_preference || ''}
                            onChange={handleInputChange}
                            className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                          >
                            <option value="">None specified</option>
                            <option value="low">Low Fat</option>
                            <option value="moderate">Moderate Fat</option>
                            <option value="high">High Fat</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="additional-notes" className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Notes
                          </label>
                          <textarea
                            id="additional-notes"
                            name="additional_notes"
                            value={preferences.additional_notes || ''}
                            onChange={handleInputChange}
                            className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            rows="4"
                            placeholder="Include any other dietary information, health goals, calorie targets, or special considerations here."
                            maxLength={500}
                          ></textarea>
                          <div className="mt-1 text-xs text-gray-500 flex justify-end">
                            {(preferences.additional_notes || '').length}/500 characters
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={updateDietaryPreferences}
                          disabled={savingPrefs}
                          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 ${savingPrefs ? 'opacity-70 cursor-not-allowed' : 'hover:bg-emerald-700'}`}
                        >
                          {savingPrefs ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            'Save Preferences'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Dietary Restrictions & Allergies</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {availableRestrictions.map((restriction) => {
                          const isSelected = restrictions.some(r => r.id === restriction.id);
                          return (
                            <div key={restriction.id} className="flex items-center">
                              <input
                                id={`restriction-${restriction.id}`}
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRestriction(restriction)}
                                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`restriction-${restriction.id}`} className="ml-2 block text-sm text-gray-700">
                                {restriction.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={updateDietaryRestrictions}
                          disabled={savingRestrictions}
                          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 ${savingRestrictions ? 'opacity-70 cursor-not-allowed' : 'hover:bg-emerald-700'}`}
                        >
                          {savingRestrictions ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            'Save Restrictions'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Analysis History Tab */}
              {activeTab === 'history' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Your Past Menu Analyses</h3>
                  {analysisHistory.length > 0 ? (
                    <div className="space-y-4">
                      {analysisHistory.map((analysis) => (
                        <div key={analysis.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-900">{analysis.restaurant_name || 'Menu Analysis'}</h4>
                              <p className="text-xs text-gray-500">{new Date(analysis.created_at).toLocaleString()}</p>
                            </div>
                            <button 
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                              onClick={() => viewAnalysisDetails(analysis.id)}
                            >
                              View Details
                            </button>
                          </div>
                          <div className="p-4">
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Restaurant:</strong> {analysis.restaurant_name || 'Unknown Restaurant'}
                              {analysis.is_follow_up && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  {analysis.follow_up_type === 'question' ? 'Follow-up Question' : 'New Analysis'}
                                </span>
                              )}
                              {analysis.tokens_consumed !== undefined && (
                                <div className="mt-1 text-xs text-gray-500">
                                  <strong>Tokens consumed:</strong> {analysis.tokens_consumed.toLocaleString()}
                                </div>
                              )}
                            </div>
                            {analysis.query_text && (
                              <div className="mb-3">
                                <p className="text-sm text-gray-600">
                                  <strong>{analysis.follow_up_type === 'question' ? 'Question:' : 'Request notes:'}</strong>{' '}
                                  {analysis.query_text.length > 100 
                                    ? `${analysis.query_text.substring(0, 100)}...` 
                                    : analysis.query_text}
                                </p>
                              </div>
                            )}
                            
                            {/* Display answer preview for follow-up questions */}
                            {analysis.follow_up_type === 'question' && analysis.answer_preview && (
                              <div className="mb-3 p-2 bg-gray-50 rounded">
                                <p className="text-sm text-gray-800">
                                  <strong>Answer: </strong>
                                  {analysis.answer_preview}
                                </p>
                              </div>
                            )}
                            
                            {/* Show recommendations count for regular analyses */}
                            {(!analysis.is_follow_up || analysis.follow_up_type === 'new_analysis') && (
                              <div className="text-sm text-gray-600 mb-2">
                                <strong>Recommendations:</strong> {analysis.item_count > 0 ? analysis.item_count : 'No recommendations'}
                              </div>
                            )}
                            
                            <div className="flex flex-wrap gap-2 mt-2">
                              {analysis.top_recommendations?.map((rec, idx) => (
                                <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
                                  {rec.dish_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg">
                      <Clock className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No analysis history</h3>
                      <p className="mt-1 text-sm text-gray-500">You haven't analyzed any menus yet.</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Account Tab */}
              {activeTab === 'account' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Account Settings</h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="text-md font-medium text-gray-800 mb-2">Account Information</h4>
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={user?.email || ''}
                          readOnly
                          className="block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm cursor-not-allowed"
                        />
                        <p className="text-sm text-gray-500 mb-4">Your email cannot be changed</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Verified
                        </label>
                        <div className="flex items-center">
                          {isEmailVerified ? (
                            <span className="text-green-600 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Verified
                            </span>
                          ) : (
                            <div>
                              <span className="text-yellow-600 flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Not Verified
                              </span>
                              <button 
                                onClick={handleResendVerification}
                                disabled={sendingVerification}
                                className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline block w-full text-right"
                              >
                                {sendingVerification ? 'Sending...' : 'Resend verification email'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subscription Plan
                        </label>
                        <div className="flex items-center">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {user?.subscription_plan || 'Free Plan'}
                          </span>
                          <button className="ml-2 text-sm text-emerald-600 hover:text-emerald-700">
                            Upgrade Plan
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">
                            <div className="flex items-center">
                              <Zap className="h-4 w-4 mr-1 text-amber-500" />
                              Monthly Credit Usage
                            </div>
                          </label>
                          <span className="text-sm text-gray-500">
                            {creditUsage.used} of {creditUsage.total} credits used
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              creditUsage.percentageUsed > 90 ? 'bg-red-500' : 
                              creditUsage.percentageUsed > 70 ? 'bg-amber-500' : 
                              'bg-emerald-500'
                            }`} 
                            style={{ width: `${Math.min(creditUsage.percentageUsed, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {creditUsage.remaining === 'Unlimited' ? (
                            'Unlimited credits with your subscription'
                          ) : (
                            <>
                              {creditUsage.remaining} credits remaining this month
                              {creditUsage.percentageUsed > 90 && (
                                <span className="text-red-600 ml-1 font-medium">
                                  (Running low! Consider upgrading your plan)
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      
                      {/* Billing Date Information */}
                      {billingDate && (
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-blue-500" />
                                Billing Date
                              </div>
                            </label>
                            <span className="text-sm font-medium text-gray-900">
                              {billingDate.getDate()}{getOrdinalSuffix(billingDate.getDate())} of each month
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Your subscription will renew automatically on the {billingDate.getDate()}{getOrdinalSuffix(billingDate.getDate())} of each month
                          </p>
                        </div>
                      )}

                      {isAdmin && (
                        <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 flex items-center">
                          <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <span className="font-medium text-purple-800">Administrator Account</span>
                            <p className="text-xs text-purple-600">You have administrative privileges</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="email-notifications"
                              name="email-notifications"
                              type="checkbox"
                              checked={notificationSettings.email_notifications}
                              onChange={(e) => setNotificationSettings({
                                ...notificationSettings,
                                email_notifications: e.target.checked
                              })}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label className="block text-sm font-medium text-gray-700">
                              <div className="flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1 text-red-500" />
                                Email notifications
                              </div>
                            </label>
                            <p className="text-gray-500">Get notified about new features and updates via email.</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="marketing-emails"
                              name="marketing-emails"
                              type="checkbox"
                              checked={notificationSettings.marketing_emails}
                              onChange={(e) => setNotificationSettings({
                                ...notificationSettings,
                                marketing_emails: e.target.checked
                              })}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="marketing-emails" className="font-medium text-gray-700">Marketing emails</label>
                            <p className="text-gray-500">Receive promotional offers and marketing communications.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={updateNotificationSettings}
                          disabled={savingNotifications}
                          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 ${savingNotifications ? 'opacity-70 cursor-not-allowed' : 'hover:bg-emerald-700'}`}
                        >
                          {savingNotifications ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            'Save Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-red-600 mb-4">Danger Zone</h3>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-red-600 mb-4">
                        Deleting your account will permanently remove all your data, including analysis history and preferences.
                      </p>
                      <button
                        type="button"
                        onClick={confirmDeleteAccount}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Invitations Tab - Only for Admin Users */}
              {isAdmin && activeTab === 'invitations' && (
                <ManageInvitationsPage />
              )}
              
              {/* Users Tab - Only for Admin Users */}
              {isAdmin && activeTab === 'users' && (
                <ManageUsersPage />
              )}
              
              {/* Plans Tab - Only for Admin Users */}
              {isAdmin && activeTab === 'plans' && (
                <ManagePlansPage />
              )}
            </>
          )}
        </div>

        {/* Version Info - Only for Admin Users */}
        {isAdmin && versionInfo && (
          <div className="mt-4 px-6 pb-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="font-semibold text-gray-700">Backend Version:</span>
                <div className="flex items-center space-x-3">
                  <span title={versionInfo.commit_hash}>
                    <span className="font-mono bg-gray-200 px-2 py-1 rounded">
                      {versionInfo.commit_short}
                    </span>
                  </span>
                  {versionInfo.branch && (
                    <span className="text-gray-500">
                      Branch: <span className="font-mono">{versionInfo.branch}</span>
                    </span>
                  )}
                  {versionInfo.commit_date && (
                    <span className="text-gray-500">
                      {new Date(versionInfo.commit_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Details Modal */}
      {selectedAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedAnalysis.restaurant_name || 'Menu Analysis'} Details
              </h3>
              <button
                onClick={closeAnalysisDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              {loadingAnalysisDetails ? (
                <div className="flex justify-center items-center py-10">
                  <svg className="animate-spin h-8 w-8 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-2 text-gray-600">Loading analysis details...</span>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">Restaurant Information</h4>
                    <p className="text-gray-700">{selectedAnalysis.restaurant_name || 'Unknown Restaurant'}</p>
                    <p className="text-xs text-gray-500 mt-1">Analyzed on {new Date(selectedAnalysis.created_at).toLocaleString()}</p>
                    
                    {/* Display dietary notes if available */}
                    {selectedAnalysis.query_text && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <h5 className="text-sm font-medium text-gray-900 mb-1">Dietary Criteria</h5>
                        <p className="text-sm text-gray-700">{selectedAnalysis.query_text}</p>
                      </div>
                    )}

                    {/* Display token usage information for all users */}
                    {selectedAnalysis.tokens_consumed !== undefined && (
                      <div className="text-xs text-blue-700 mt-1 p-1 bg-blue-50 rounded">
                        <span className="font-semibold">Tokens consumed:</span> {selectedAnalysis.tokens_consumed.toLocaleString()}
                      </div>
                    )}
                    
                    {selectedAnalysis.image_url && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Menu Image</h4>
                        {selectedAnalysis.image_url.toLowerCase().endsWith('.pdf') ? (
                          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <p className="text-sm text-gray-600 mb-2">PDF document available:</p>
                            <a 
                              href={`${config.apiUrl}${selectedAnalysis.image_url}`} 
                              target="_blank"
                              rel="noopener noreferrer" 
                              className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View PDF
                            </a>
                          </div>
                        ) : (
                          <img 
                            src={`${config.apiUrl}${selectedAnalysis.image_url}`} 
                            alt="Menu" 
                            className="max-w-full h-auto rounded-lg border border-gray-200"
                            style={{ maxHeight: '300px' }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                    <div className="space-y-4">
                      {selectedAnalysis.recommendations?.map((rec, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium text-gray-900">{rec.dish_name}</h5>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
                              Match: {rec.match_score}%
                            </span>
                          </div>
                          {rec.description && (
                            <p className="text-sm text-gray-700 mt-2">{rec.description}</p>
                          )}
                          {rec.explanation && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-sm text-gray-700"><strong>Why Recommended:</strong> {rec.explanation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={closeAnalysisDetails}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Delete your account?</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This action cannot be undone. Once you delete your account, all of your data will be permanently removed from our servers.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                >
                  {deletingAccount ? (
                    'Deleting...'
                  ) : (
                    'Yes, delete my account'
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancelDeleteAccount}
                  disabled={deletingAccount}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
